import type { ConversationDTO, PublicUser } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { conversationApi } from '@/api/endpoints';
import { useAuth } from '@/stores/auth';
import { targetKey, useMessages } from '@/stores/messages';
import { useConversations } from '@/stores/social';
import { confirm, toast } from '@/stores/ui';
import { useUsers } from '@/stores/users';
import { navigateTo } from './navigation';

/** The people in a conversation besides you, with their latest profiles. */
export function recipientsOf(conversation: Pick<ConversationDTO, 'recipients'>, users = useUsers.getState().users): PublicUser[] {
  return conversation.recipients.map((r) => users[r.id] ?? r);
}

/** A group's name, or its members' names ("Ben, Cara and Dave") when it has none. */
export function conversationTitle(conversation: Pick<ConversationDTO, 'type' | 'name' | 'recipients'>, users = useUsers.getState().users): string {
  if (conversation.type === 'GROUP' && conversation.name) return conversation.name;
  const names = recipientsOf(conversation, users).map((u) => u.displayName);
  if (names.length === 0) return 'Just you';
  if (names.length === 1) return names[0]!;
  if (names.length <= 3) return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} others`;
}

/** Group members, you included, ordered by who joined first (the server's order). */
export function useGroupMembers(conversationId: string | undefined, me: PublicUser | null): PublicUser[] {
  const conversation = useConversations((s) => (conversationId ? s.conversations[conversationId] : undefined));
  const users = useUsers((s) => s.users);
  if (!conversation) return [];
  return [...(me ? [users[me.id] ?? me] : []), ...recipientsOf(conversation, users)];
}

/* ------------------------------------------------------------------ */
/* Group actions                                                       */
/* ------------------------------------------------------------------ */

/** Leaves a group after asking. Resolves true when you left. */
export async function leaveGroup(conversation: ConversationDTO): Promise<boolean> {
  const me = useAuth.getState().user;
  if (!me) return false;
  const last = conversation.recipients.length === 0;
  const ok = await confirm({
    title: `Leave ${conversationTitle(conversation)}?`,
    body: last
      ? "You're the last one here, so the group and its messages will be deleted."
      : "You won't see its messages any more unless someone adds you back.",
    confirmLabel: 'Leave group',
    danger: true,
  });
  if (!ok) return false;
  try {
    await conversationApi.removeMember(conversation.id, me.id);
    useConversations.getState().remove(conversation.id);
    useMessages.getState().forget(targetKey({ conversationId: conversation.id }));
    if (location.pathname === `/app/@me/${conversation.id}`) navigateTo('/app/@me');
    return true;
  } catch (err) {
    toast.error(errorMessage(err, 'Could not leave the group.'));
    return false;
  }
}

export async function removeFromGroup(conversation: ConversationDTO, user: PublicUser) {
  const ok = await confirm({
    title: `Remove ${user.displayName}?`,
    body: `They'll leave ${conversationTitle(conversation)} and won't see its messages any more.`,
    confirmLabel: 'Remove',
    danger: true,
  });
  if (!ok) return;
  try {
    await conversationApi.removeMember(conversation.id, user.id);
  } catch (err) {
    toast.error(errorMessage(err, 'Could not remove them.'));
  }
}

export async function makeGroupOwner(conversation: ConversationDTO, user: PublicUser) {
  const ok = await confirm({
    title: `Make ${user.displayName} the owner?`,
    body: "They'll be able to remove people from the group, and you won't.",
    confirmLabel: 'Make owner',
  });
  if (!ok) return;
  try {
    useConversations.getState().upsert(await conversationApi.updateGroup(conversation.id, { ownerId: user.id }));
  } catch (err) {
    toast.error(errorMessage(err, 'Could not change the owner.'));
  }
}

export async function renameGroup(conversation: ConversationDTO, name: string): Promise<boolean> {
  try {
    useConversations.getState().upsert(await conversationApi.updateGroup(conversation.id, { name: name.trim() || null }));
    return true;
  } catch (err) {
    toast.error(errorMessage(err, 'Could not rename the group.'));
    return false;
  }
}
